import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client.js';
import { useTenantScope } from '../context/TenantScopeContext.jsx';
import './FrictionMap.css';

const hierarchyLevels = [
  { level: 'Junta', scope: 'Vista agregada', detail: 'CID preventivo, tendencia de friccion y areas prioritarias.' },
  { level: 'Empresa', scope: 'Mapa global', detail: 'Distribucion de estabilidad, carga y maestria por unidad.' },
  { level: 'Area', scope: 'Drill down operativo', detail: 'Equipos en verde, amarillo y rojo con evolucion semanal.' },
  { level: 'Supervisor', scope: 'Guia del lider', detail: 'Pausas conscientes, preguntas sugeridas y seguimiento.' },
  { level: 'Trabajador', scope: 'Detalle autorizado', detail: 'Metadatos, capsula EPA y recomendacion individual.' },
];

const domainDescriptions = {
  vitalidad: 'Energia fisica, descanso, fatiga y carga somatica.',
  vinculos: 'Relaciones con pares, clima y pertenencia.',
  nucleo_afectivo: 'Soporte emocional profundo y estabilidad personal.',
  estabilidad_economica: 'Presion financiera y percepcion de futuro.',
};

export default function FrictionMap() {
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailType, setDetailType] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const fmtMoney = (value) => `$${Number(value || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;
  const fmtRisk = (value) => `${Math.round(Number(value || 0) * 100)}%`;

  const { tenantId } = useTenantScope();

  useEffect(() => {
    loadSummary();
  }, [tenantId]);

  async function loadSummary() {
    setLoading(true);
    setError('');
    try {
      const res = await api.getFrictionMapSummary();
      setSummary(res.data);
    } catch (err) {
      setError(err.message || 'No fue posible cargar el mapa.');
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(type, id) {
    if (!id) return;
    setDetailLoading(true);
    setDetailType(type);
    try {
      const loaders = {
        area: api.getFrictionArea,
        supervisor: api.getFrictionSupervisor,
        worker: api.getFrictionWorker,
      };
      const res = await loaders[type](id);
      setDetail(res.data);
    } catch (err) {
      setError(err.message || 'No fue posible cargar el detalle.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  const domains = useMemo(() => {
    const items = summary?.domains || [];
    if (!items.length) {
      return [
        { key: 'vitalidad', label: 'Vitalidad', percentage: 0, count: 0 },
        { key: 'vinculos', label: 'Vinculos', percentage: 0, count: 0 },
        { key: 'nucleo_afectivo', label: 'Nucleo afectivo', percentage: 0, count: 0 },
        { key: 'estabilidad_economica', label: 'Estabilidad economica', percentage: 0, count: 0 },
      ];
    }
    return items;
  }, [summary]);

  const global = summary?.global;

  return (
    <div className="friction-map-page">
      <header className="friction-hero">
        <div>
          <h1>Mapa de Fricción Sistémica</h1>
          <p>
            Inteligencia preventiva para leer patrones de energia, friccion, carga,
            riesgo, estabilidad y maestria antes de que la desconexion se convierta en rotacion.
          </p>
        </div>
        <div className="friction-hero-panel" aria-label="Resumen del modulo">
          <div>
            <span className="panel-label">Privacidad</span>
            <strong>Solo metadatos</strong>
          </div>
          <div>
            <span className="panel-label">Drill down</span>
            <strong>Junta a trabajador</strong>
          </div>
          <div>
            <span className="panel-label">Salida</span>
            <strong>Guia + EPA</strong>
          </div>
        </div>
      </header>

      {error && (
        <div className="friction-alert" role="alert">
          {error}
          <button type="button" onClick={loadSummary}>Reintentar</button>
        </div>
      )}

      <section className="friction-metrics" aria-label="Indicadores de fricción">
        <article className={global ? `level-${global.level}` : ''}>
          <span>Indice preventivo</span>
          <strong>{loading ? '...' : fmtRisk(global?.avgRisk)}</strong>
          <p>{global?.level === 'red' ? 'Friccion prioritaria activa.' : 'Lectura agregada del sistema.'}</p>
        </article>
        <article>
          <span>CID preventivo</span>
          <strong>{loading ? '...' : fmtMoney(global?.cidPreventive)}</strong>
          <p>No se mezcla con rotacion consumada.</p>
        </article>
        <article>
          <span>CID evitable estimado</span>
          <strong>{loading ? '...' : fmtMoney(global?.cidAvoidable)}</strong>
          <p>Potencial asociado a intervencion oportuna.</p>
        </article>
      </section>

      {global?.leaderGuide && (
        <section className="friction-guide">
          <div>
            <span>Guia inmediata del lider</span>
            <h2>{global.leaderGuide.action}</h2>
            <p>{global.leaderGuide.question}</p>
          </div>
          <strong>{global.leaderGuide.followUp}</strong>
        </section>
      )}

      <section className="friction-section">
        <div className="section-heading">
          <h2>Lectura jerárquica</h2>
          <p>Desde la visión de la empresa hasta el detalle operativo por área, supervisor y trabajador.</p>
        </div>
        <div className="hierarchy-grid">
          {hierarchyLevels.map((item) => (
            <article key={item.level} className="hierarchy-card">
              <span>{item.scope}</span>
              <h3>{item.level}</h3>
              <p>{item.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="friction-section">
        <div className="section-heading">
          <h2>Dominios de carga</h2>
          <p>Taxonomía privada para direccionar mensajes y formación sin etiquetar clínicamente al trabajador.</p>
        </div>
        <div className="domain-list">
          {domains.map((domain) => (
            <article key={domain.key} className="domain-row">
              <div>
                <h3>{domain.label}</h3>
                <p>{domainDescriptions[domain.key]}</p>
              </div>
              <span>{domain.percentage}%</span>
            </article>
          ))}
        </div>
      </section>

      <section className="friction-section">
        <div className="section-heading">
          <h2>Areas Y Supervisores</h2>
          <p>Primer corte real desde encuestas, alertas, ausentismo y costo preventivo.</p>
        </div>
        <div className="friction-split">
          <div className="compact-table">
            <h3>Areas prioritarias</h3>
            {(summary?.byArea || []).slice(0, 6).map((area) => (
              <button
                type="button"
                key={area.id || area.label}
                className={`compact-row level-${area.level}`}
                onClick={() => loadDetail('area', area.id)}
              >
                <span>{area.label}</span>
                <strong>{fmtRisk(area.avgRisk)}</strong>
                <em>{fmtMoney(area.cidPreventive)}</em>
              </button>
            ))}
          </div>
          <div className="compact-table">
            <h3>Supervisores</h3>
            {(summary?.bySupervisor || []).slice(0, 6).map((supervisor) => (
              <button
                type="button"
                key={supervisor.id || supervisor.label}
                className={`compact-row level-${supervisor.level}`}
                onClick={() => loadDetail('supervisor', supervisor.id)}
              >
                <span>{supervisor.label}</span>
                <strong>{fmtRisk(supervisor.avgRisk)}</strong>
                <em>{supervisor.total} personas</em>
              </button>
            ))}
          </div>
        </div>
      </section>

      {(detail || detailLoading) && (
        <section className="friction-section detail-panel">
          <div className="section-heading">
            <h2>{detailLoading ? 'Cargando detalle' : detail?.worker?.name || detail?.summary?.label}</h2>
            <p>
              {detailType === 'worker'
                ? 'Detalle autorizado con metadatos, guia del lider y capsula EPA.'
                : 'Drill down operativo por nivel jerarquico.'}
            </p>
          </div>

          {!detailLoading && detail?.summary && (
            <>
              <div className="friction-metrics nested">
                <article className={`level-${detail.summary.level}`}>
                  <span>Indice preventivo</span>
                  <strong>{fmtRisk(detail.summary.avgRisk)}</strong>
                  <p>{detail.summary.total} personas evaluadas.</p>
                </article>
                <article>
                  <span>CID preventivo</span>
                  <strong>{fmtMoney(detail.summary.cidPreventive)}</strong>
                  <p>Estimacion agregada del nivel seleccionado.</p>
                </article>
                <article>
                  <span>Guia</span>
                  <strong>{detail.summary.leaderGuide?.code}</strong>
                  <p>{detail.summary.leaderGuide?.question}</p>
                </article>
              </div>
              <div className="worker-list">
                {(detail.workers || []).slice(0, 12).map((worker) => (
                  <button
                    type="button"
                    key={worker.id}
                    className={`worker-row level-${worker.frictionLevel}`}
                    onClick={() => loadDetail('worker', worker.id)}
                  >
                    <div>
                      <h3>{worker.name}</h3>
                      <p>{worker.areaName} / {worker.loadDomainLabel} / {worker.frictionLabel}</p>
                    </div>
                    <strong>{fmtRisk(worker.riskScore)}</strong>
                    <span>{fmtMoney(worker.cidPreventiveEstimate)}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {!detailLoading && detail?.worker && (
            <div className="worker-detail-grid">
              <article>
                <span>Riesgo</span>
                <strong>{fmtRisk(detail.scores.risk)}</strong>
                <p>{detail.worker.frictionLabel}</p>
              </article>
              <article>
                <span>Maestria</span>
                <strong>{fmtRisk(detail.scores.mastery)}</strong>
                <p>{detail.signals.energyPattern}</p>
              </article>
              <article>
                <span>Dominio</span>
                <strong>{detail.signals.loadDomainLabel}</strong>
                <p>{detail.signals.languagePattern}</p>
              </article>
              <article className="wide">
                <span>Pregunta poderosa</span>
                <strong>{detail.leaderGuide.question}</strong>
                <p>{detail.leaderGuide.action}</p>
              </article>
              <article className="wide">
                <span>{detail.epaCapsule.title}</span>
                <strong>{detail.epaCapsule.durationMinutes} minutos</strong>
                <p>{detail.epaCapsule.steps.join(' ')}</p>
              </article>
            </div>
          )}
        </section>
      )}

      <section className="friction-section">
        <div className="section-heading">
          <h2>Detalle Autorizado</h2>
          <p>Trabajadores con friccion amarilla o roja. No se muestran textos sensibles ni diagnosticos clinicos.</p>
        </div>
        <div className="worker-list">
          {(summary?.priorityWorkers || []).length === 0 && (
            <p className="empty-state">Sin trabajadores en friccion prioritaria con los datos actuales.</p>
          )}
          {(summary?.priorityWorkers || []).map((worker) => (
            <button
              type="button"
              key={worker.id}
              className={`worker-row level-${worker.frictionLevel}`}
              onClick={() => loadDetail('worker', worker.id)}
            >
              <div>
                <h3>{worker.name}</h3>
                <p>{worker.areaName} / {worker.loadDomainLabel} / {worker.frictionLabel}</p>
              </div>
              <strong>{fmtRisk(worker.riskScore)}</strong>
              <span>{fmtMoney(worker.cidPreventiveEstimate)}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="friction-playbook">
        <div className="playbook-card">
          <span>Guia del lider</span>
          <h2>El lider como arriero</h2>
          <p>
            El sistema sugerira pausas conscientes, preguntas breves y seguimiento temporal.
            No dara ordenes ni emitira diagnosticos.
          </p>
        </div>
        <div className="playbook-card accent">
          <span>Capsula EPA</span>
          <h2>5 minutos de maestria</h2>
          <p>
            Escucha, Procesa y Ajusta para convertir friccion en accion concreta,
            sin lenguaje de victimizacion.
          </p>
        </div>
      </section>
    </div>
  );
}
